/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, skipWhile, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { PendoTrackEventService } from '../../../external/services';
import { EPendoCustomEvents, IClusteringEnabledUpdate } from '../../models';
import { updateLayoutPreferences } from '../../operators/layout-preferences.operator';
import { LayoutPreferencesService, WindowResizeService } from '../../services';
import {
  EAdvancedOptionsActions,
  ECustomerMetadataActions,
  ELayoutActions,
  ELayoutPreferencesActions,
  EMapOptionsActions,
  EVehicleActions,
  EVehicleDriverPanelActions,
  EVehicleListPanelActions,
  EWorkingTimeDirectiveActions,
  GetLayoutPreferences,
  GetLayoutPreferencesSuccess,
  GetMapHierarchySuccess,
  SwitchMapType,
  UpdateDriverStatusSortType,
  UpdateDriverWorkingStateFilter,
  UpdateGarminStopStatusesIdsSelected,
  UpdateGarminStopStatusesIdsSelectedSilent,
  UpdateIsBreakDueSelectedFilter,
  UpdateIsClusteringByDefaultTriggered,
  UpdateIsClusteringEnabled,
  UpdateIsGarminStopsEnabled,
  UpdateIsGeofenceEnabled,
  UpdateIsShowLabelsEnabled,
  UpdateIsShowTachoInformation,
  UpdateIsShowVehicleBalloonLinksEnabled,
  UpdateIsSuggestedGeofenceEnabled,
  UpdateIsTrafficEnabled,
  UpdateIsVehicleDriverPanelCollapsed,
  UpdateIsWorkOrdersEnabled,
  UpdatePlaceCategoryIds,
  UpdatePlaceCategoryIdsSilent,
  UpdateVehicleDriverPanelActiveTab,
  UpdateVehicleSelectionMode,
  UpdateVehiclesSortType
} from '../actions';
import {
  getAccessiblePlotCount,
  getExternalModuleLoaded,
  getHasClusteringBeenManuallyUpdated,
  getIsClusteringByDefaultFeatureToggleEnabled
} from '../selectors';
import { defaultLayoutPreferences, IAppState, ILayoutPreferencesState } from '../state';

@Injectable()
export class LayoutPreferencesEffects {
  public getLayoutPreferences$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetLayoutPreferences>(ELayoutPreferencesActions.GetLayoutPreferences),
      map(() => {
        const layoutPreferences = this._layoutPreferencesService.get({ ...defaultLayoutPreferences });
        return new GetLayoutPreferencesSuccess(layoutPreferences);
      })
    )
  );

  public updateVehicleDriverPanelActiveTab$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehicleDriverPanelActiveTab>(EVehicleDriverPanelActions.UpdateActiveTab),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          vehicleDriverPanel: {
            ...state.vehicleDriverPanel,
            activeTab: action.payload
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateVehicleDriverPanelIsCollapsed$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsVehicleDriverPanelCollapsed>(EVehicleDriverPanelActions.UpdateIsCollapsed),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          vehicleDriverPanel: {
            ...state.vehicleDriverPanel,
            isCollapsed: action.payload
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsSuggestedGeofenceEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsSuggestedGeofenceEnabled>(EMapOptionsActions.UpdateIsSuggestedGeofenceEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, isSuggestedGeofencesEnabled: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsTrafficEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsTrafficEnabled>(EMapOptionsActions.UpdateIsTrafficEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, isTrafficEnabled: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public UpdateIsGeofenceEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsGeofenceEnabled>(EMapOptionsActions.UpdateIsGeofenceEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, isGeofencesEnabled: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsGarminStopsEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsGarminStopsEnabled>(EMapOptionsActions.UpdateIsGarminStopsEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            isGarminStopsEnabled: action.payload
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsWorkOrdersEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsWorkOrdersEnabled>(EMapOptionsActions.UpdateIsWorkOrdersEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, isWorkOrdersEnabled: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsClusteringEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsClusteringEnabled>(EMapOptionsActions.UpdateIsClusteringEnabled),
      updateLayoutPreferences(
        (action: { payload: IClusteringEnabledUpdate }, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            isClusteringEnabled: action.payload.enabled,
            hasClusteringBeenManuallyUpdated: state.mapOptions.hasClusteringBeenManuallyUpdated ? true : action.payload.manuallyUpdated
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public UpdatePlaceCategoryIds$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdatePlaceCategoryIds | UpdatePlaceCategoryIdsSilent>(
        EAdvancedOptionsActions.UpdatePlaceCategoryIds,
        EAdvancedOptionsActions.UpdatePlaceCategoryIdsSilent
      ),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            advancedOptions: {
              ...state.mapOptions.advancedOptions,
              placeCategoryIds: action.payload
            }
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateGarminStopStatusesIdsSelected$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateGarminStopStatusesIdsSelected | UpdateGarminStopStatusesIdsSelectedSilent>(
        EAdvancedOptionsActions.UpdateGarminStopStatusesIdsSelected,
        EAdvancedOptionsActions.UpdateGarminStopStatusesIdsSelectedSilent
      ),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            advancedOptions: {
              ...state.mapOptions.advancedOptions,
              garminStopStatusesIdsSelected: action.payload ? action.payload : []
            }
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsShowLabelsEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsShowLabelsEnabled>(EMapOptionsActions.UpdateIsShowLabelsEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, isShowLabelsEnabled: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateVehicleSelectionMode$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehicleSelectionMode>(EVehicleListPanelActions.UpdateVehicleSelectionMode),
      updateLayoutPreferences(
        (action: UpdateVehicleSelectionMode, state: ILayoutPreferencesState) => ({
          ...state,
          vehicleListPanel: {
            ...state.vehicleListPanel,
            selectionMode: action.payload
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateVehiclesSortType$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehiclesSortType>(EVehicleActions.UpdateVehiclesSortType),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          vehiclesSortType: action.payload
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateMapType$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<SwitchMapType>(ELayoutActions.SwitchMapType),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, mapType: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsShowTachoInformationEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsShowTachoInformation>(EMapOptionsActions.UpdateIsShowTachoInformationEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            isShowTachoInformationEnabled: action.payload
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateDriverWorkingStateFilter$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateDriverWorkingStateFilter>(EWorkingTimeDirectiveActions.UpdateDriverWorkingStateFilter),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            workingTimeDirective: {
              ...state.mapOptions.workingTimeDirective,
              filter: action.payload
            }
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsBreakDueSelectedFilter$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsBreakDueSelectedFilter>(EWorkingTimeDirectiveActions.UpdateIsBreakDueSelectedFilter),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            workingTimeDirective: {
              ...state.mapOptions.workingTimeDirective,
              filterBreakDue: action.payload
            }
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateDriverStatusSortType$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateDriverStatusSortType>(EWorkingTimeDirectiveActions.UpdateDriverStatusSortType),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: {
            ...state.mapOptions,
            workingTimeDirective: {
              ...state.mapOptions.workingTimeDirective,
              sortType: action.payload
            }
          }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public updateIsShowVehicleBalloonLinksEnabled$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateIsShowVehicleBalloonLinksEnabled>(EMapOptionsActions.UpdateIsShowVehicleBalloonLinksEnabled),
      updateLayoutPreferences(
        (action: any, state: ILayoutPreferencesState) => ({
          ...state,
          mapOptions: { ...state.mapOptions, isShowVehicleBalloonLinksEnabled: action.payload }
        }),
        this._store,
        this._layoutPreferencesService
      )
    )
  );

  public trackClusterEnabledPendoEvent$: Observable<Action> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<GetLayoutPreferencesSuccess | UpdateIsClusteringEnabled>(
          ELayoutPreferencesActions.GetLayoutPreferencesSuccess,
          EMapOptionsActions.UpdateIsClusteringEnabled
        ),
        filter(() => isPlatformBrowser(this._platformId)),
        switchMap(action =>
          this._store.pipe(
            select(getExternalModuleLoaded),
            skipWhile(isExternalLoaded => !isExternalLoaded),
            take(1),
            map(() => action)
          )
        ),
        tap(action => {
          const enabled = action instanceof GetLayoutPreferencesSuccess ? action.payload.mapOptions.isClusteringEnabled : action.payload;
          this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.ClusteringEnabled, { enabled });
        })
      ),
    { dispatch: false }
  );

  public setClusteringByDefaultFromHierarchy$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetMapHierarchySuccess>(ECustomerMetadataActions.GetMapHierarchySuccess),
      filter(() => isPlatformBrowser(this._platformId)),
      withLatestFrom(
        this._store.pipe(select(getIsClusteringByDefaultFeatureToggleEnabled)),
        this._store.pipe(select(getAccessiblePlotCount)),
        this._windowResizeService.isMobile$,
        this._store.pipe(select(getHasClusteringBeenManuallyUpdated))
      ),
      switchMap(([_, isClusteringByDefaultFeatureToggleEnabled, plottableCount, isMobile, hasClusteringBeenManuallyUpdated]) => {
        let actions: Action[] = [];
        const exceededThreshold = plottableCount > parseInt(environment.config.clusteringByDefaultMaxPlots, 10);
        const shouldClusterByDefault = isClusteringByDefaultFeatureToggleEnabled && (isMobile || exceededThreshold);

        if (shouldClusterByDefault && !hasClusteringBeenManuallyUpdated) {
          actions = [
            new UpdateIsClusteringEnabled({ enabled: true, manuallyUpdated: false }),
            new UpdateIsClusteringByDefaultTriggered(true)
          ];
          this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.ClusteringDefaultThresholdExceeded, {
            exceeded: true,
            plottableCount
          });
        } else if (isClusteringByDefaultFeatureToggleEnabled) {
          actions = [new UpdateIsClusteringByDefaultTriggered(false)];
        }

        return actions;
      })
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<IAppState>,
    private readonly _layoutPreferencesService: LayoutPreferencesService,
    private readonly _pendoTrackEventService: PendoTrackEventService,
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    private readonly _windowResizeService: WindowResizeService
  ) {}
}
